import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ExternalLink, 
  Github, 
  Zap, 
  Shield, 
  Code, 
  Search,
  Smartphone,
  Terminal,
  Eye,
  Play,
  Star
} from 'lucide-react';

const Projects = ({ data }) => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const categories = ['all', 'Security Tool', 'Forensics Tool', 'Mobile Security', 'Web Security'];
  
  const filteredProjects = filter === 'all' 
    ? data.featured 
    : data.featured.filter(project => project.category === filter);

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Security Tool': Shield,
      'Forensics Tool': Search,
      'Mobile Security': Smartphone,
      'Web Security': Code,
      'default': Terminal
    };
    return iconMap[category] || iconMap.default;
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      'Security Tool': 'text-green-400 bg-green-400/10 border-green-400/30',
      'Forensics Tool': 'text-purple-400 bg-purple-400/10 border-purple-400/30',
      'Mobile Security': 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
      'Web Security': 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      'default': 'text-gray-400 bg-gray-400/10 border-gray-400/30'
    };
    return colorMap[category] || colorMap.default;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="projects" className="py-20 bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent flex-1 max-w-xs" />
            <Terminal className="w-8 h-8 text-cyan-400 mx-4" />
            <div className="h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent flex-1 max-w-xs" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-cyan-400 mb-4">
            Security Projects
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Open source security tools and projects built to enhance cybersecurity practices and digital forensics.
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
        >
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setFilter(category)}
              variant={filter === category ? "default" : "outline"}
              className={`
                ${filter === category 
                  ? 'bg-cyan-500 text-black hover:bg-cyan-400' 
                  : 'border-gray-600 text-gray-300 hover:border-cyan-400 hover:text-cyan-400'
                } 
                transition-all duration-300 font-medium
              `}
            >
              {category === 'all' ? 'All Projects' : category}
            </Button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence>
            {filteredProjects.map((project, index) => {
              const IconComponent = getCategoryIcon(project.category);
              const categoryColor = getCategoryColor(project.category);
              
              return (
                <motion.div
                  key={project.id}
                  variants={itemVariants}
                  layout
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedProject(project)}
                >
                  <Card className="bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300 h-full overflow-hidden">
                    {/* Project Image */}
                    <div className="relative overflow-hidden">
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Status Badge */}
                      <div className="absolute top-4 left-4">
                        <Badge className={`${project.status === 'Live' ? 'bg-green-500' : 'bg-yellow-500'} text-black font-semibold`}>
                          <div className="w-2 h-2 bg-current rounded-full mr-1 animate-pulse" />
                          {project.status}
                        </Badge>
                      </div>

                      {/* Category Icon */}
                      <div className={`absolute top-4 right-4 p-2 rounded-lg border ${categoryColor}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                    </div>

                    <CardHeader>
                      <CardTitle className="flex items-start justify-between">
                        <span className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                          {project.title}
                        </span>
                        <Eye className="w-5 h-5 text-gray-400 group-hover:text-cyan-400 transition-colors duration-300 flex-shrink-0 ml-2" />
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-300 mb-4 line-clamp-3">
                        {project.description}
                      </p>
                      
                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.slice(0, 3).map((tech, techIndex) => (
                          <Badge 
                            key={techIndex}
                            variant="outline" 
                            className="text-xs border-gray-600 text-gray-300"
                          >
                            {tech}
                          </Badge>
                        ))}
                        {project.technologies.length > 3 && (
                          <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                            +{project.technologies.length - 3}
                          </Badge>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        {project.githubUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-gray-600 text-gray-300 hover:border-green-400 hover:text-green-400 transition-all duration-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(project.githubUrl, '_blank');
                            }}
                          >
                            <Github className="w-4 h-4 mr-1" />
                            Code
                          </Button>
                        )}
                        {project.demoUrl && (
                          <Button
                            size="sm"
                            className="bg-cyan-500 text-black hover:bg-cyan-400 transition-colors duration-300"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(project.demoUrl, '_blank');
                            }}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Demo
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Project Modal */}
        <AnimatePresence>
          {selectedProject && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedProject(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-gray-900 border border-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.title}
                    className="w-full h-64 object-cover rounded-t-lg"
                  />
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{selectedProject.title}</h3>
                  <p className="text-gray-300 mb-4">{selectedProject.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-white mb-2">Key Features</h4>
                    <ul className="space-y-1">
                      {selectedProject.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-center text-gray-300">
                          <Star className="w-4 h-4 text-green-400 mr-2 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-2">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.technologies.map((tech, index) => (
                        <Badge key={index} className="bg-gray-800 text-green-400 border-green-400/30">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    {selectedProject.githubUrl && (
                      <Button
                        className="bg-gray-800 text-white hover:bg-gray-700 border border-gray-600"
                        onClick={() => window.open(selectedProject.githubUrl, '_blank')}
                      >
                        <Github className="w-4 h-4 mr-2" />
                        View Code
                      </Button>
                    )}
                    {selectedProject.demoUrl && (
                      <Button
                        className="bg-cyan-500 text-black hover:bg-cyan-400"
                        onClick={() => window.open(selectedProject.demoUrl, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Live Demo
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const X = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default Projects;